import { useHandler } from "@redotech/react-util/hook";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { Modal } from "../../modal";
import {
  HsaPurchaseType,
  HsaSubscriptionSignals,
  hsaLearnMoreSteps,
  hsaLearnMoreTabs,
} from "./hsa-learn-more-content";
import * as css from "./hsa-learn-more-modal.module.css";

const ASSET_BASE = "https://assets.getredo.com/conversion/hsa/";

const TAB_OPTIONS = [
  { value: HsaPurchaseType.SUBSCRIPTION, el: "Subscription" },
  { value: HsaPurchaseType.ONE_TIME, el: "One-time" },
];

/** The HSA/FSA walkthrough the PDP badge and cart widget open. Render it in a
 * `ModalRootContext` outside the widget's shadow root, or themes clip it. */
export const HsaLearnMoreModal = memo(function HsaLearnMoreModal({
  requiresLmn,
  subscriptionSignals,
  open,
  onClose,
}: {
  requiresLmn: boolean;
  subscriptionSignals: HsaSubscriptionSignals;
  open: boolean;
  onClose: () => void;
}) {
  const tabs = hsaLearnMoreTabs(subscriptionSignals);
  // Cleared on close, so each open starts on the tab the current cart calls for.
  const [chosen, setChosen] = useState<HsaPurchaseType>();
  const purchaseType = tabs.tabbed
    ? (chosen ?? tabs.initial)
    : HsaPurchaseType.ONE_TIME;
  const steps = hsaLearnMoreSteps(purchaseType, requiresLmn);

  const handleClose = useHandler(() => {
    setChosen(undefined);
    onClose();
  });

  return (
    <Modal onClose={handleClose} open={open}>
      <Flex
        className={css.modalContent}
        dir="column"
        gap="2xl"
        overflowY="auto"
        p="3xl"
      >
        <Flex align="center" dir="row" gap="md" justify="space-between">
          <Text
            as="h2"
            className={css.title}
            fontSize="3xl"
            fontWeight="medium"
            textColor="primary"
          >
            Pay with your HSA/FSA
          </Text>
          <RedoButton
            aria-label="Close"
            hierarchy="tertiary"
            IconLeading={CloseIcon}
            onClick={handleClose}
            size="md"
          />
        </Flex>
        {tabs.tabbed && (
          <RedoButtonTabGroup
            onChange={setChosen}
            options={TAB_OPTIONS}
            size="md"
            value={purchaseType}
          />
        )}
        <ol className={css.steps}>
          {steps.map((step, index) => (
            <li className={css.step} key={step.image}>
              <span aria-hidden className={css.stepNumber}>
                {index + 1}
              </span>
              <Text
                as="h3"
                className={css.stepTitle}
                fontSize="lg"
                fontWeight="medium"
                textColor="primary"
              >
                {step.title}
              </Text>
              <div className={css.stepImageWell}>
                <img
                  alt=""
                  className={css.stepImage}
                  src={`${ASSET_BASE}${step.image}`}
                />
              </div>
            </li>
          ))}
        </ol>
      </Flex>
    </Modal>
  );
});
